import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Calendar, Zap, Bell } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import weekGeneratorService from '../../services/weekGeneratorService';

/**
 * AutoGenerationSettings - Paramètres de génération automatique
 */
export function AutoGenerationSettings({
  isOpen,
  onClose,
  settings,
  onUpdateSettings,
  customTemplates = []
}) {
  const templates = weekGeneratorService.getAvailableTemplates();
  const daysOfWeek = [
    { value: 1, label: 'Lundi' },
    { value: 2, label: 'Mardi' },
    { value: 3, label: 'Mercredi' },
    { value: 4, label: 'Jeudi' },
    { value: 5, label: 'Vendredi' },
    { value: 6, label: 'Samedi' },
    { value: 0, label: 'Dimanche' }
  ];

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Génération automatique"
      size="large"
    >
      <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">
        {/* Activation */}
        <div className="bg-gradient-to-r from-cyan-950/30 to-purple-950/30 border border-cyan-500/30 rounded-xl p-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <span className="text-sm font-medium text-white block">Génération automatique</span>
                <span className="text-xs text-zinc-400">
                  {settings.enabled ? 'Activée' : 'Désactivée'}
                </span>
              </div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => onUpdateSettings({ enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-zinc-700 rounded-full peer peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-purple-500 transition-all" />
              <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
            </div>
          </label>
        </div>

        {settings.enabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            {/* Mode */}
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">
                Comportement
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onUpdateSettings({ mode: 'ask' })}
                  className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    settings.mode === 'ask'
                      ? 'bg-cyan-500/10 border-cyan-500 text-cyan-300'
                      : 'bg-zinc-800/30 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <Bell className="w-4 h-4" />
                  <div className="text-left">
                    <span className="text-sm font-medium block">Me demander</span>
                    <span className="text-[10px] opacity-70">Confirmation requise</span>
                  </div>
                </button>
                <button
                  onClick={() => onUpdateSettings({ mode: 'auto' })}
                  className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    settings.mode === 'auto'
                      ? 'bg-purple-500/10 border-purple-500 text-purple-300'
                      : 'bg-zinc-800/30 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <Zap className="w-4 h-4" />
                  <div className="text-left">
                    <span className="text-sm font-medium block">Automatique</span>
                    <span className="text-[10px] opacity-70">Sans confirmation</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Jour de génération */}
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">
                Jour de génération
              </label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map(day => (
                  <button
                    key={day.value}
                    onClick={() => onUpdateSettings({ dayOfWeek: day.value })}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      settings.dayOfWeek === day.value
                        ? 'bg-cyan-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    {day.label.substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>

            {/* Template */}
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">
                Template à utiliser
              </label>
              <div className="space-y-2">
                {/* Templates par défaut */}
                {templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => onUpdateSettings({ template: template.id, templateId: null })}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                      settings.template === template.id && !settings.templateId
                        ? 'bg-zinc-700/50 border-zinc-500'
                        : 'bg-zinc-800/30 border-zinc-700/50 hover:border-zinc-600'
                    }`}
                  >
                    <Calendar className="w-5 h-5 text-zinc-400" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-zinc-200 block">{template.name}</span>
                      <span className="text-xs text-zinc-500">~{template.tasksPerDay} tâches/jour</span>
                    </div>
                    {settings.template === template.id && !settings.templateId && (
                      <div className="w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </button>
                ))}

                {/* Templates personnalisés */}
                {customTemplates.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 py-2">
                      <div className="flex-1 h-px bg-zinc-800" />
                      <span className="text-xs text-zinc-600">Mes templates</span>
                      <div className="flex-1 h-px bg-zinc-800" />
                    </div>
                    {customTemplates.map(template => (
                      <button
                        key={template.id}
                        onClick={() => onUpdateSettings({ templateId: template.id })}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                          settings.templateId === template.id
                            ? 'bg-amber-500/10 border-amber-500'
                            : 'bg-zinc-800/30 border-zinc-700/50 hover:border-zinc-600'
                        }`}
                      >
                        <span className="text-2xl">{template.icon}</span>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-zinc-200 block">{template.name}</span>
                          <span className="text-xs text-zinc-500">{template.taskCount} tâches</span>
                        </div>
                        {settings.templateId === template.id && (
                          <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Options supplémentaires */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">
                Options
              </label>

              <OptionToggle
                label="Inclure les weekends"
                checked={settings.includeWeekends}
                onChange={(v) => onUpdateSettings({ includeWeekends: v })}
              />
              <OptionToggle
                label="Ajouter les tâches d'hygiène"
                checked={settings.autoAddHygiene}
                onChange={(v) => onUpdateSettings({ autoAddHygiene: v })}
              />
              <OptionToggle
                label="Ajouter les pauses"
                checked={settings.autoAddBreaks}
                onChange={(v) => onUpdateSettings({ autoAddBreaks: v })}
              />
            </div>
          </motion.div>
        )}

        {/* Info */}
        <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4">
          <p className="text-xs text-zinc-500 leading-relaxed">
            {settings.enabled
              ? settings.mode === 'auto'
                ? `Chaque ${daysOfWeek.find(d => d.value === settings.dayOfWeek)?.label.toLowerCase()}, ton planning sera généré automatiquement.`
                : `Chaque ${daysOfWeek.find(d => d.value === settings.dayOfWeek)?.label.toLowerCase()}, on te demandera si tu veux générer ton planning.`
              : 'Active la génération automatique pour ne plus jamais oublier de planifier ta semaine !'
            }
          </p>
        </div>
      </div>
    </BottomSheet>
  );
}

/**
 * OptionToggle - Toggle simple
 */
function OptionToggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between p-3 bg-zinc-800/30 border border-zinc-700/50 rounded-lg cursor-pointer hover:bg-zinc-800/50 transition-all">
      <span className="text-sm text-zinc-300">{label}</span>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:bg-cyan-500 transition-all" />
        <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform" />
      </div>
    </label>
  );
}

export default AutoGenerationSettings;
